/**
 * Utility functions for detecting third-party scripts and pixels in ad tags
 */

export interface DetectedScript {
  vendor: string;
  found: boolean;
  details?: string;
  location: 'tag' | 'iframe' | 'adm';
  url?: string;
}

/**
 * Vendor patterns for third-party script detection
 * Aligned with backend TagMacroizer.VENDOR_DOMAINS and MEASUREMENT_HOSTS
 */
const VENDOR_PATTERNS: Record<string, string[]> = {
  // InMarket's own domains
  'InMarket': ['imbid.co', 'inmarket.com', 'inmarkethub.com', 'thinknearhub.com', 'thinknear.com'],
  // MRAID standard
  'MRAID': ['mraid.js'],
  // Third-party verification & measurement
  'DoubleVerify': ['doubleverify.com'],
  'IAS': ['adsafeprotected.com', 'integralads.com', 'iasds01.com', 'iaspetas.com', 'integral.co'],
  'Moat': ['moatads.com', 'moatpixel.com'],
  'comScore': ['scorecardresearch.com'],
  'Nielsen': ['imrworldwide.com'],
  'DCM': ['doubleclick.net'],
  'Google': ['googlesyndication.com', 'googletagservices.com'],
  'Flashtalking': ['flashtalking.com'],
  'Sizmek': ['serving-sys.com'],
  'LiveRamp': ['rlcdn.com'],
  'ABC Insights': ['abcsinsights.com'],
  'Adloox': ['adlooxtracking.com'],
  'Adform': ['adform.net', 'adformdsp.net'],
  'NinthDecimal': ['ninthdecimal.com'],
  // Ad servers
  'Celtra': ['celtra.com'],
  'Innovid': ['innovid.com'],
  'Extreme Reach': ['extremereach.io', 'extremereach.com'],
};

/**
 * Detect third-party scripts in content
 * @param content - HTML content or tag to analyze
 * @param location - Where the content is found ('tag', 'iframe', or 'adm')
 * @returns Array of detected scripts
 */
export function detectThirdPartyScripts(
  content: string,
  location: 'tag' | 'iframe' | 'adm' = 'adm'
): DetectedScript[] {
  if (!content) return [];

  const results: DetectedScript[] = [];
  const lowerContent = content.toLowerCase();

  // Check for vendor patterns (each vendor can have multiple domain patterns)
  Object.entries(VENDOR_PATTERNS).forEach(([vendor, patterns]) => {
    const matchedPattern = patterns.find((pattern) => lowerContent.includes(pattern));
    if (matchedPattern) {
      // Try to extract the URL containing this pattern
      const urlMatch = content.match(
        new RegExp(`(https?://[^"\\s<>'"]*${matchedPattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}[^"\\s<>'"]*)`, 'i')
      );

      results.push({
        vendor,
        found: true,
        location,
        url: urlMatch ? urlMatch[1] : undefined,
      });
    }
  });

  // Special handling for Kantar/Bluecava with PID extraction
  if (lowerContent.includes('sync.graph.bluecava.com')) {
    const urlMatch = content.match(
      /(https?:\/\/[^"\\s<>'"]*sync\.graph\.bluecava\.com[^"\\s<>'"]*)/i
    );
    const pid = content.match(/PID=([^&"\\s<>'"]+)/)?.[1] || '';
    
    results.push({
      vendor: 'Kantar/Bluecava',
      found: true,
      details: pid ? `Placement ID: ${pid}` : undefined,
      location,
      url: urlMatch ? urlMatch[1] : undefined,
    });
  }

  // Remove duplicates (same vendor in same location)
  const uniqueResults = results.filter(
    (result, index, self) =>
      index ===
      self.findIndex(
        (t) => t.vendor === result.vendor && t.location === result.location
      )
  );

  return uniqueResults;
}

/**
 * Detect scripts in ADM content (for preview responses)
 * Analyzes the full ADM string for third-party scripts
 */
export function detectScriptsInAdm(adm: string): DetectedScript[] {
  if (!adm) return [];

  // Check the full ADM content
  return detectThirdPartyScripts(adm, 'adm');
}

/**
 * Detect scripts in tag content (for raw tags)
 * Splits tag around <ins> tags and checks both parts
 */
export function detectScriptsInTag(tag: string): DetectedScript[] {
  if (!tag) return [];

  const results: DetectedScript[] = [];

  // Check content before <ins>
  const beforeIns = tag.split('<ins')[0];
  if (beforeIns) {
    results.push(...detectThirdPartyScripts(beforeIns, 'tag'));
  }

  // Check content after </ins>
  const afterIns = tag.split('</ins>')[1];
  if (afterIns) {
    results.push(...detectThirdPartyScripts(afterIns, 'tag'));
  }

  const scriptMatches = Array.from(tag.matchAll(/<script[^>]*>([\s\S]*?)<\/script>/gi));
  scriptMatches.forEach((match) => {
    const scriptContent = match[1] || '';
    const scriptSrc = match[0].match(/src=["']([^"']+)["']/i)?.[1] || '';

    if (scriptSrc) {
      results.push(...detectThirdPartyScripts(scriptSrc, 'tag'));
    }
    if (scriptContent) {
      results.push(...detectThirdPartyScripts(scriptContent, 'tag'));
    }
  });

  // Remove duplicates
  return results.filter(
    (result, index, self) =>
      index ===
      self.findIndex(
        (t) => t.vendor === result.vendor && t.location === result.location
      )
  );
}

/**
 * Detect vendor from a URL string
 * Used to augment backend assets that are missing vendor_hint
 * @param url - The URL to analyze
 * @returns The vendor name if detected, or null
 */
export function detectVendorFromUrl(url: string): string | null {
  if (!url) return null;

  const lowerUrl = url.toLowerCase();

  const matchedEntry = Object.entries(VENDOR_PATTERNS).find(([, patterns]) =>
    patterns.some((pattern) => lowerUrl.includes(pattern.toLowerCase()))
  );

  return matchedEntry ? matchedEntry[0] : null;
}
